// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import axios2 from 'utils/axios2';
import { dispatch } from '../index';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    loading: false,

    // API Keys
    apiKeys: [],
    apiKeyStats: {},

    // Webhooks
    webhooks: [],
    webhookStats: {},
    webhookDeliveries: [],

    // Metrics
    gatewayMetrics: {
        total_requests_24h: 0,
        successful_requests_24h: 0,
        failed_requests_24h: 0,
        average_response_time: 0,
        active_api_keys: 0,
        requests_by_hour: [],
        top_endpoints: [],
        status_codes: {}
    },

    // Rate Limiting
    rateLimitStatus: {},

    // Tenants
    tenants: [],
    selectedTenant: null
};

const slice = createSlice({
    name: 'apiGateway',
    initialState,
    reducers: {
        // Loading
        setLoading(state, action) {
            state.loading = action.payload;
        },

        // Error handling
        hasError(state, action) {
            state.error = action.payload;
        },

        // API Keys
        setApiKeys(state, action) {
            state.apiKeys = action.payload;
        },

        addApiKey(state, action) {
            state.apiKeys.push(action.payload);
        },

        updateApiKey(state, action) {
            const index = state.apiKeys.findIndex((key) => key.id === action.payload.id);
            if (index !== -1) {
                state.apiKeys[index] = action.payload;
            }
        },

        removeApiKey(state, action) {
            state.apiKeys = state.apiKeys.filter((key) => key.id !== action.payload);
        },

        setApiKeyStats(state, action) {
            state.apiKeyStats = action.payload;
        },

        // Webhooks
        setWebhooks(state, action) {
            state.webhooks = action.payload;
        },

        addWebhook(state, action) {
            state.webhooks.push(action.payload);
        },

        updateWebhook(state, action) {
            const index = state.webhooks.findIndex((webhook) => webhook.id === action.payload.id);
            if (index !== -1) {
                state.webhooks[index] = action.payload;
            }
        },

        removeWebhook(state, action) {
            state.webhooks = state.webhooks.filter((webhook) => webhook.id !== action.payload);
        },

        setWebhookStats(state, action) {
            state.webhookStats = action.payload;
        },

        setWebhookDeliveries(state, action) {
            state.webhookDeliveries = action.payload;
        },

        // Metrics
        setGatewayMetrics(state, action) {
            state.gatewayMetrics = action.payload;
        },

        // Rate Limiting
        setRateLimitStatus(state, action) {
            state.rateLimitStatus = action.payload;
        },

        // Tenants
        setTenants(state, action) {
            state.tenants = action.payload;
        },

        setSelectedTenant(state, action) {
            state.selectedTenant = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;

// Actions
export const {
    setLoading,
    hasError,
    setApiKeys,
    addApiKey,
    updateApiKey,
    removeApiKey,
    setApiKeyStats,
    setWebhooks,
    addWebhook,
    updateWebhook,
    removeWebhook,
    setWebhookStats,
    setWebhookDeliveries,
    setGatewayMetrics,
    setRateLimitStatus,
    setTenants,
    setSelectedTenant
} = slice.actions;

// ----------------------------------------------------------------------

// API Key Actions
export function fetchApiKeys() {
    return async () => {
        try {
            dispatch(setLoading(true));

            const response = await axios2.get('/api/v1/api-keys', {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(setApiKeys(response.data));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error fetching API keys:', error);
            dispatch(hasError(error.message));
        } finally {
            dispatch(setLoading(false));
        }
    };
}

export function createApiKey(keyData) {
    return async () => {
        try {
            dispatch(setLoading(true));

            const response = await axios2.post('/api/v1/api-keys', keyData, {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(addApiKey(response.data.api_key));
            dispatch(hasError(null));

            // Return the full response including secret key
            return response.data;
        } catch (error) {
            console.error('Error creating API key:', error);
            dispatch(hasError(error.message));
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

export function updateApiKeyAction(keyId, keyData) {
    return async () => {
        try {
            dispatch(setLoading(true));

            const response = await axios2.put(`/api/v1/api-keys/${keyId}`, keyData, {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(updateApiKey(response.data));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error updating API key:', error);
            dispatch(hasError(error.message));
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

export function deleteApiKey(keyId) {
    return async () => {
        try {
            dispatch(setLoading(true));

            await axios2.delete(`/api/v1/api-keys/${keyId}`, {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(removeApiKey(keyId));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error deleting API key:', error);
            dispatch(hasError(error.message));
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

export function fetchApiKeyStats(keyId) {
    return async () => {
        try {
            const response = await axios2.get(`/api/v1/api-keys/${keyId}/usage`, {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(setApiKeyStats(response.data));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error fetching API key stats:', error);
            dispatch(hasError(error.message));
        }
    };
}

// Webhook Actions
export function fetchWebhooks() {
    return async () => {
        try {
            dispatch(setLoading(true));

            const response = await axios2.get('/api/v1/webhooks', {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(setWebhooks(response.data));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error fetching webhooks:', error);
            dispatch(hasError(error.message));
        } finally {
            dispatch(setLoading(false));
        }
    };
}

export function createWebhook(webhookData) {
    return async () => {
        try {
            dispatch(setLoading(true));

            const response = await axios2.post('/api/v1/webhooks', webhookData, {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(addWebhook(response.data));
            dispatch(hasError(null));

            return response.data;
        } catch (error) {
            console.error('Error creating webhook:', error);
            dispatch(hasError(error.message));
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

export function updateWebhookAction(webhookId, webhookData) {
    return async () => {
        try {
            dispatch(setLoading(true));

            const response = await axios2.put(`/api/v1/webhooks/${webhookId}`, webhookData, {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(updateWebhook(response.data));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error updating webhook:', error);
            dispatch(hasError(error.message));
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

export function deleteWebhook(webhookId) {
    return async () => {
        try {
            dispatch(setLoading(true));

            await axios2.delete(`/api/v1/webhooks/${webhookId}`, {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(removeWebhook(webhookId));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error deleting webhook:', error);
            dispatch(hasError(error.message));
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

export function testWebhook(webhookId) {
    return async () => {
        try {
            const response = await axios2.post(
                `/api/v1/webhooks/${webhookId}/test`,
                {},
                {
                    headers: {
                        'X-Tenant-ID': '1' // TODO: Get from auth context
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error('Error testing webhook:', error);
            throw error;
        }
    };
}

export function fetchWebhookDeliveries(webhookId) {
    return async () => {
        try {
            const response = await axios2.get(`/api/v1/webhooks/${webhookId}/deliveries`, {
                headers: {
                    'X-Tenant-ID': '1' // TODO: Get from auth context
                }
            });

            dispatch(setWebhookDeliveries(response.data));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error fetching webhook deliveries:', error);
            dispatch(hasError(error.message));
        }
    };
}

// Gateway Metrics Actions
export function fetchGatewayMetrics() {
    return async () => {
        try {
            const response = await axios2.get('/api/v1/gateway/metrics', {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('api_key')}` // TODO: Get from auth context
                }
            });

            dispatch(setGatewayMetrics(response.data));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error fetching gateway metrics:', error);
            dispatch(hasError(error.message));
        }
    };
}

export function fetchRateLimitStatus() {
    return async () => {
        try {
            const response = await axios2.get('/api/v1/gateway/rate-limit/status', {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('api_key')}` // TODO: Get from auth context
                }
            });

            dispatch(setRateLimitStatus(response.data));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error fetching rate limit status:', error);
            dispatch(hasError(error.message));
        }
    };
}

// Tenant Actions
export function fetchTenants() {
    return async () => {
        try {
            const response = await axios2.get('/api/v1/api-keys/tenants');

            dispatch(setTenants(response.data));
            dispatch(hasError(null));
        } catch (error) {
            console.error('Error fetching tenants:', error);
            dispatch(hasError(error.message));
        }
    };
}

export function createTenant(tenantData) {
    return async () => {
        try {
            dispatch(setLoading(true));

            const response = await axios2.post('/api/v1/api-keys/tenants', tenantData);

            dispatch(setTenants([...initialState.tenants, response.data]));
            dispatch(hasError(null));

            return response.data;
        } catch (error) {
            console.error('Error creating tenant:', error);
            dispatch(hasError(error.message));
            throw error;
        } finally {
            dispatch(setLoading(false));
        }
    };
}

// Utility Actions
export function clearError() {
    return () => {
        dispatch(hasError(null));
    };
}

export function resetApiGatewayState() {
    return () => {
        dispatch(setApiKeys([]));
        dispatch(setWebhooks([]));
        dispatch(setGatewayMetrics(initialState.gatewayMetrics));
        dispatch(setRateLimitStatus({}));
        dispatch(hasError(null));
        dispatch(setLoading(false));
    };
}
